import { CreateDictionaryRecordDto, QueryFactory } from '@lmt/shared-types';
import { createId } from '@lmt/unicorn-shared/app/helpers/create-id';
export type OnTagsChangeEvent = { target: { value: string } };

const convertTagsRegex = /(\s|^)(#(\S+?))\s/g;
const failedToCreateTags: string[] = [];
export function isAnyNewTags(comment: string): boolean {
  const converted = parseNonExistingTags(comment);
  return converted.tagsToCreate.length > 0;
}
export async function convertTags(
  comment: string,
  setComment: (changeFunction: (comment: string) => string) => void,
  existingTags: { display: string; id: string }[],
): Promise<void> {
  const converted = parseNonExistingTags(comment);

  const tagsToCreate: { temporaryId: string; tagWithoutHash: string }[] = [];
  for (const tagToCreate of converted.tagsToCreate) {
    const existingTag = existingTags.find(
      (x) =>
        x.display.toLowerCase() ===
        '#' + tagToCreate.tagWithoutHash.toLowerCase(),
    );
    if (existingTag) {
      converted.comment = converted.comment.replaceAll(
        `{${tagToCreate.temporaryId}}`,
        `{${existingTag.id}}`,
      );
    } else {
      tagsToCreate.push(tagToCreate);
    }
  }
  setComment(() => converted.comment);

  for (const tagToCreate of tagsToCreate) {
    try {
      const createdTag = await QueryFactory.TagClient.create({
        value: tagToCreate.tagWithoutHash,
      });
      setComment((com) => {
        return com.replaceAll(
          `{${tagToCreate.temporaryId}}`,
          `{${createdTag.id}}`,
        );
      });
    } catch (e) {
      // if we failed to create a tag - remove the tag decorations
      failedToCreateTags.push(tagToCreate.tagWithoutHash);
      setComment((com) => {
        return com
          .replaceAll(`{${tagToCreate.temporaryId}}`, ``)
          .replaceAll(
            `\[#${tagToCreate.tagWithoutHash}\]`,
            '#' + tagToCreate.tagWithoutHash,
          );
      });
    }
  }
}

export function parseNonExistingTags(comment: string): {
  comment: string;
  tagsToCreate: { temporaryId: string; tagWithoutHash: string }[];
} {
  const newTags = comment.matchAll(convertTagsRegex);

  const tagsToCreate: { temporaryId: string; tagWithoutHash: string }[] = [];
  for (const regexResult of newTags) {
    const newTag = regexResult[2];
    const tagWithoutHash = newTag.slice(1);

    const temporaryId = createId();
    if (failedToCreateTags.includes(tagWithoutHash)) continue;
    comment = comment
      .replaceAll(newTag, `[${newTag}]{${temporaryId}}`)
      .replaceAll(`[[${newTag}]{${temporaryId}}]`, `[${newTag}]`);
    tagsToCreate.push({
      temporaryId: temporaryId,
      tagWithoutHash: tagWithoutHash,
    });
  }
  return { comment, tagsToCreate };
}

const splitTagsRegex = /\[#(\w*)\]\{(\d*)\}/g;
type SplitTagsStringResult =
  | {
      text: string;
      isTag: true;
      tagId: number | undefined;
    }
  | {
      text: string;
      isTag: false;
    };
export function splitTagString(
  stringWithTags: string,
): SplitTagsStringResult[] {
  const split = stringWithTags.split(splitTagsRegex);
  const result: SplitTagsStringResult[] = [];
  let index = 0;

  while (index < split.length) {
    if (split[index] !== '') {
      result.push({
        text: split[index],
        isTag: false,
      });
    }
    index++;
    if (index >= split.length) continue;
    result.push({
      text: '#' + split[index],
      isTag: true,
      tagId: parseInt(split[index + 1]),
    });
    index += 2;
  }
  return result;
}
